import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import ClosIcon from "src/assets/svg-icons/close.svg";
import {
  useAddContactPersonMutation,
  useUpdateContactPersonMutation,
} from "src/api/clients";
import { useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { IContactPerson } from "src/types/clients";
import { emailRegex } from "src/constants/regex";

export const schema = yup.object().shape({
  name: yup
    .string()
    .required("Required")
    .max(100, "Can not exceed 100 characters"),
  position: yup.string().max(100, "Can not exceed 100 characters"),
  mobileNumber: yup.string().max(100, "Can not exceed 100 characters"),
  email: yup
    .string()
    .required("Required")
    .max(100, "Can not exceed 100 characters")
    .test("is-valid-email", "Invalid Email", (value) => {
      if (!value) return true;
      return emailRegex.test(value);
    }),
});

export interface IFormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  onClose: any;
  dataToEdit?: IContactPerson;
  refetch: Function;
}

const AddEditContactPersonPopup: React.FC<IProps> = (props) => {
  const { open, onClose, dataToEdit, refetch } = props;
  const { id: contactId } = dataToEdit || {};
  const { id: clientId } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !dataToEdit ? "add" : "edit";

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<IFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  // APIs
  const { mutate, status, isPending, error } = useAddContactPersonMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateContactPersonMutation();

  const handleClose = () => {
    onClose();
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    const dataToSend = {
      clientId: `${clientId}`,
      ...data,
      mobileNumber: data.mobileNumber?.trim()
        ? `${data.mobileNumber?.trim()}`
        : null,
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({
        ...dataToSend,
        id: contactId,
      });
    }
  };

  useEffect(() => {
    if (dataToEdit) {
      reset({
        ...dataToEdit,
        mobileNumber: dataToEdit.mobileNumber ?? "",
      });
    }
  }, [dataToEdit]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The contact person has been added successfully!",
        },
      ]);
      handleClose();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.data?.errors?.[0]?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "The contact person has been updated successfully!",
        },
      ]);
      handleClose();
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data?.errors?.[0]?.message ||
            "Oops! Something went wrong.",
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={handleClose}
      component={"div"}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          bgcolor: "#191919",
        },
      }}
    >
      <DialogTitle sx={{ p: "30px 30px 20px 30px" }}>
        <Box display={"flex"} justifyContent={"flex-end"}>
          <Typography fontSize={"22px"} fontWeight={600} lineHeight={"22px"}>
            {mode === "add" ? "Add New Contact Person" : "Edit Contact Person"}
          </Typography>
          <IconButton size="small" sx={{ ml: "auto" }} onClick={handleClose}>
            <img src={ClosIcon} alt="" />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack px={"30px"} gap={"21px"}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Name"
                fullWidth
                size="medium"
                {...field}
                error={!!errors.name}
                helperText={errors.name?.message}
                InputLabelProps={{
                  shrink: field.value !== undefined,
                }}
              />
            )}
          />
          <Controller
            name="position"
            control={control}
            render={({ field }) => (
              <TextField
                label="Position"
                fullWidth
                size="medium"
                {...field}
                error={!!errors.position}
                helperText={errors.position?.message}
                InputLabelProps={{
                  shrink: field.value !== undefined,
                }}
              />
            )}
          />
          <Controller
            name="mobileNumber"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                label="Mobile Number"
                fullWidth
                size="medium"
                {...field}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber?.message}
                InputLabelProps={{
                  shrink: field.value !== undefined,
                }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                label="Email"
                fullWidth
                size="medium"
                {...field}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{
                  shrink: field.value !== undefined,
                }}
                disabled={mode === "edit"}
              />
            )}
          />
        </Stack>

        <DialogActions sx={{ padding: "31px", gap: "12px" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={isPending || isPendingUpdate}
            variant="contained"
            type="submit"
          >
            {mode === "add" ? "Save" : "Update"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditContactPersonPopup;
